<script setup>
import notification from "@/utils/plugins/NotificationService";
import { ref, onMounted, onBeforeMount } from 'vue'
import { useUiStore } from "@/app/stores/uiStore";
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { useProjectsSearchStore } from '@/app/stores/projectsSearchStore'

import ProjectsService from '@/api/services/projects.service'
import DepartmentService from '@/api/services/department.service'
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import HeaderTitle from '../../components/layout/HeaderTitle.vue'
import SharedButtonPlus from '@/components/shared/SharedButtonPlus.vue'

const uiStore = useUiStore();
const router = useRouter()
const projectsSearchStore = useProjectsSearchStore()

// Estado
// const id = ref('')
const name = ref('')
const client = ref('')
const type = ref('')
const archived = ref(false)
const minConsumedTime = ref(null)
const source = ref('')
const ownerDepartment = ref('')
const optionsClient = ref([])
const optionsType = ref([])
const optionsDepartment = ref([])
const optionsSource = [
  { label: 'Connectus', value: 'connectus' },
  { label: 'Clockify', value: 'clockify' },
]
const projects = ref([])
const page = ref(1)
const itemsPerPage = ref(10)
const totalResults = ref(0)
const tag = ref('')

// Métodos
const getTypes = async () => {
  // uiStore.setLoading(true);
  uiStore.startLoading();
  try {
    const result = await ProjectsService.getTypes()
    optionsType.value = result.data['hydra:member']
  } catch (err) {
    console.error('Types', err)
    notification.addNotification({
      title: 'Proyectos',
      message: 'Error al obtener los tipos de proyecto' + err,
      type: 'error',
      duration: 6000
    })
    uiStore.stopLoading();
  } finally {
    // uiStore.setLoading(false)
    uiStore.stopLoading();
  }
}

const getClients = async () => {
  // uiStore.setLoading(true)
  uiStore.startLoading();
  try {
    const result = await ProjectsService.getClients()
    optionsClient.value = result.data['hydra:member']
  } catch (err) {
    console.error('Types', err)
    notification.addNotification({
      title: 'Proyectos',
      message: 'Error al obtener los clientes' + err,
      type: 'error',
      duration: 6000
    })
    uiStore.stopLoading();
  } finally {
    // uiStore.setLoading(false)
    uiStore.stopLoading();
  }
}

const getDepartments = async () => {
  try {
    const result = await DepartmentService.getDepartments({
      page: 1,
      itemsPerPage: 9999,
      'order[name]': 'ASC',
    })
    optionsDepartment.value = result.data['hydra:member']
  } catch (err) {
    notification.addNotification({
      title: 'Proyectos',
      message: 'Error al obtener los departamentos ' + err,
      type: 'error',
      duration: 6000
    })
  }
}

const onSearch = async () => {
  tag.value = tag.value ? tag.value.slice(0, 5) : ''

  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    archived: archived.value,
    'order[name]': 'ASC'
  }

  if (name.value) params.name = name.value
  if (type.value?.name) params['projectType.name'] = type.value.name
  if (client.value?.name) params['client.name'] = client.value.name
  if (tag.value) params.tag = tag.value
  if (source.value) params.source = source.value
  if (ownerDepartment.value?.id) params['ownerDepartment.id'] = ownerDepartment.value.id
  if (minConsumedTime.value === true) {
    params['minConsumptionHours[gt]'] = 0
    params['reviewPeriodMonths[gt]'] = 0
  }

  // uiStore.setLoading(true)
  uiStore.startLoading();
  try {
    const result = await ProjectsService.getProjects(params)
    projects.value = result.data['hydra:member']
    totalResults.value = result.data['hydra:totalItems']
  } catch (err) {
    notification.addNotification({
      title: 'Proyectos',
      message: 'Error al obtener los proyectos ' + err,
      type: 'error',
      duration: 6000
    })
    uiStore.stopLoading();
  } finally {
    // uiStore.setLoading(false)
    uiStore.stopLoading();
  }
}

const formatSource = (value) => {
  const selectedSource = optionsSource.find((item) => item.value === value)
  return selectedSource?.label || '--'
}

const syncClockifyProjects = async () => {
  // uiStore.setLoading(true)
  uiStore.startLoading();
  try {
    const result = await ProjectsService.syncClockifyProjects()
    console.log('Sync Clockify Projects OK', result)
  } catch (err) {
    console.error('Error SyncClockifyProjects', err)
    notification.addNotification({
      title: 'Proyectos',
      message: 'Error al sincronizar los proyectos de clockify.',
      type: 'error',
      duration: 6000
    })
    uiStore.stopLoading();
  } finally {
    // uiStore.setLoading(false)
    uiStore.stopLoading();
  }
}

// Hooks del ciclo de vida
onBeforeMount(() => {
  getTypes()
  getClients()
  getDepartments()
})

onMounted(() => {
  applyStoredFilters()
  onSearch()
  // syncClockifyProjects()
})

// Guardar solo el recorrido listado -> detalle; no persiste al recargar.
onBeforeRouteLeave((to) => {
  if (to.name !== 'show-projects') return

  projectsSearchStore.saveFilters({
    name: name.value,
    client: client.value || null,
    type: type.value || null,
    archived: archived.value,
    minConsumedTime: minConsumedTime.value,
    source: source.value,
    ownerDepartment: ownerDepartment.value || null,
    tag: tag.value,
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  })
})

const applyStoredFilters = () => {
  const filters = projectsSearchStore.filters
  if (!filters) return

  name.value = filters.name || ''
  client.value = filters.client || ''
  type.value = filters.type || ''
  archived.value = filters.archived ?? false
  minConsumedTime.value = filters.minConsumedTime ?? null
  source.value = filters.source || ''
  ownerDepartment.value = filters.ownerDepartment || ''
  tag.value = filters.tag || ''
  page.value = filters.page || 1
  itemsPerPage.value = filters.itemsPerPage || 10
}

const openProject = (project) => {
  return router.push({ name: 'show-projects', params: { id: project.id } })
}

const handlePageChange = (newPage) => {
  page.value = newPage
  onSearch()
}
</script>

<template>
  <div>
    <header-title title="Producción" />
    <div class="d-flex flex-row justify-content-start my-2">
      <svg id="icon-search" xmlns="http://www.w3.org/2000/svg" class="search-icon" viewBox="0 0 1024 1024">
        <path
          d="M641.8 737.4c-66.4 47.3-147.7 75.2-235.5 75.2-224.4 0-406.3-181.9-406.3-406.3s181.9-406.3 406.3-406.3c224.4 0 406.3 181.9 406.3 406.3 0 87.8-27.8 169.1-75.2 235.5l266.9 266.9c26.3 26.3 26.1 68.4 0 94.5l-1.1 1.1c-26 26-68.4 26.1-94.5 0l-266.9-266.9zM406.3 717c171.6 0 310.7-139.1 310.7-310.7s-139.1-310.7-310.7-310.7c-171.6 0-310.7 139.1-310.7 310.7s139.1 310.7 310.7 310.7v0z" />
      </svg>
      <div class="title-text ml-4">
        Proyectos
      </div>
    </div>
    <el-row>
      <el-card class="card-search w-100" body-style="padding:0">
        <div class="row">
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="d-flex flex-column flex-nowrap w-100 label-common">
              <div class="form-item">
                <label for="name">Nombre</label>
                <el-input type="text" placeholder="Ingrese nombre del proyecto" v-model="name" clearable
                  @change="onSearch()" />
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="d-flex flex-nowrap flex-column  w-100 label-common">
              <div class="form-item">
                <label class="w-100">Cliente</label>
                <el-select v-model="client" placeholder="Seleccione un cliente" clearable size="large" class="w-100"
                  :value-key="'@id'" filterable @change="onSearch()">
                  <el-option v-for="item in optionsClient" :key="item.id" :label="item.name" :value="item" />
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="d-flex flex-nowrap flex-column w-100 label-common">
              <div class="form-item">
                <label class="w-100">Fuente</label>
                <el-select v-model="source" placeholder="TODOS" clearable size="large" class="w-100" filterable
                  @change="onSearch()">
                  <el-option v-for="item in optionsSource" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="w-100">
              <div class="form-item">
                <label for="tag">Etiqueta</label>
                <el-input id="tag" type="text" placeholder="Ingrese la etiqueta" v-model="tag" maxlength="5" clearable
                  @change="onSearch()" />
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="w-100">
              <div class="d-flex flex-column flex-nowrap">
                <div class="form-item w-100">
                  <label for="projectType.id" class="w-100">Tipo</label>
                  <el-select size="large" class="w-100" v-model="type" placeholder="Seleccione un tipo" clearable
                    :value-key="'@id'" filterable @change="onSearch()">
                    <el-option v-for="item in optionsType" :key="item.id" :label="item.name" :value="item" />
                  </el-select>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="w-100">
              <div class="d-flex flex-column flex-nowrap">
                <div class="form-item w-100">
                  <label for="ownerDepartment" class="w-100">Propietario</label>
                  <el-select id="ownerDepartment" size="large" class="w-100" v-model="ownerDepartment"
                    placeholder="TODOS" clearable :value-key="'@id'" filterable @change="onSearch()">
                    <el-option v-for="item in optionsDepartment" :key="item.id" :label="item.name" :value="item" />
                  </el-select>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="w-100">
              <div class="d-flex flex-column flex-nowrap">
                <div class="form-item w-100">
                  <label for="projectType.id" class="w-100">Archivado</label>
                  <el-select size="large" class="w-100" v-model="archived" placeholder="TODOS" clearable filterable
                    @change="onSearch()">
                    <el-option :value="true" label="Si" />
                    <el-option :value="false" label="No" />
                  </el-select>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 col-12 d-flex flex-column justify-content-start">
            <div class="w-100">
              <div class="d-flex flex-column flex-nowrap">
                <div class="form-item w-100">
                  <label for="projectType.id" class="w-100">Consumo mínimo</label>
                  <el-select size="large" class="w-100" v-model="minConsumedTime" placeholder="TODOS" clearable
                    filterable @change="onSearch()">
                    <el-option :value="true" label="Si" />
                  </el-select>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="ml-auto d-flex justify-content-end align-items-end search-actions">
            <div class="mt-3">
              <button class="ml-auto btn btn_red search" @click="router.push({ name: 'create-project' })">
                NUEVO PROYECTO
              </button>
            </div>
            <div class="mt-3">
              <button class="ml-auto btn btn_yellow search" @click="syncClockifyProjects()">
                Sincronizar
              </button>
            </div>
            <div class="mt-3">
              <button class="ml-auto btn btn_yellow search" @click="onSearch()">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>

    <el-row>
      <div class="table table-users table-not-mobile">
        <div v-if="projects && projects.length > 0">
          <table>
            <thead>
              <tr>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div>NOMBRE</div>
                  </div>
                </th>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div>CLIENTE</div>
                  </div>
                </th>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div class="ml-2">TIPO</div>
                  </div>
                </th>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div class="ml-2">FUENTE</div>
                  </div>
                </th>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div class="ml-2">PROPIETARIO</div>
                  </div>
                </th>
                <th>
                  <div class="d-flex flex-row justify-content-start align-items-center ml-4">
                    <div class="mx-2">
                      <img src="@/assets/icons/pc-icon.svg" width="20" height="20" alt="name-icon" />
                    </div>
                    <div class="ml-2">ETIQUETA</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects" :key="project.id" class="pointer" @click="openProject(project)">
                <td>
                  <div class="row-table-image-name-link">
                    <div class="link-name">
                      <router-link :to="{ name: 'show-projects', params: { id: project.id } }" @click.stop>
                        {{ project.name }}
                      </router-link>
                    </div>
                  </div>
                </td>
                <td class="pointer">{{ project.client.name }}</td>
                <td class="pointer">{{ project.projectType.name }}</td>
                <td class="pointer">{{ formatSource(project.source) }}</td>
                <td class="pointer">{{ project.ownerDepartment?.name || '--' }}</td>
                <td class="pointer">
                  <div class="d-flex justify-content-between">
                    <div>{{ project.tag }}</div>
                    <div class="d-flex" @click.stop>
                      <shared-button-plus @onClick="openProject(project)" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <h5>No se han encontrado proyectos según los filtros especificados...</h5>
        </div>
        <SharedElementPagination class="pagination" v-model:current-page="page"
          :total="totalResults" v-model:page-size="itemsPerPage" @change="handlePageChange" show-always />
      </div>

      <div class="table-mobile w-100">
        <div v-if="projects && projects.length > 0">
          <el-row v-for="project in projects" :key="project.id" class="mt-3">
            <el-card class="project-mobile-card w-100 pointer" role="link" tabindex="0"
              :aria-label="'Ver proyecto: ' + project.name" @click="openProject(project)"
              @keydown.enter.prevent="openProject(project)">
              <div class="project-mobile-card__content">
                <div class="project-mobile-card__header">
                  <div class="project-mobile-card__title">
                    {{ project.name }}
                  </div>
                </div>
                <div class="project-mobile-card__item">
                  <span><b class="project-mobile-card__label">Cliente:</b>
                    {{ project.client?.name || '--' }}</span>
                </div>
                <div class="project-mobile-card__item">
                  <span><b class="project-mobile-card__label">Tipo:</b>
                    {{ project.projectType?.name || '--' }}</span>
                </div>
                <div class="project-mobile-card__item">
                  <span><b class="project-mobile-card__label">Fuente:</b>
                    {{ formatSource(project.source) }}</span>
                </div>
                <div class="project-mobile-card__item">
                  <span><b class="project-mobile-card__label">Departamento:</b>
                    {{ project.ownerDepartment?.name || '--' }}</span>
                </div>
                <div class="project-mobile-card__item">
                  <span><b class="project-mobile-card__label">Etiqueta:</b>
                    {{ project.tag || '--' }}</span>
                </div>
              </div>
            </el-card>
          </el-row>
        </div>
        <div v-else class="mt-3">
          <h5>No se han encontrado proyectos segÃºn los filtros especificados...</h5>
        </div>
        <SharedElementPagination class="pagination" v-model:current-page="page"
          :total="totalResults" v-model:page-size="itemsPerPage" @change="handlePageChange" show-always />
      </div>
    </el-row>
  </div>
</template>

<style lang="scss" scoped>
.title-text {
  text-align: left;
  font-size: 3em;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: $font-blue-color;

}

#icon-search {
  fill: $bg-blue;
  width: 3%;

}

#icon-nombre {
  width: 5%;
  height: 5%;
}

.bombilla {
  float: right;
  width: 60%;
  height: 90%;

  /* border-top-right-radius: 20px; */
}

.card-search {
  border-radius: 15px;
  height: 285px;
  padding: 40px;
  background: url('../../assets/icons/bombilla.png'), white;
  background-repeat: no-repeat;
  background-position-x: 100%;
  overflow: visible !important;


}

.label-common {
  label {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -2px;


  }


}

.search-input {
  width: 100%;
}

.search {
  width: 20em;
  height: 3em;
  font-weight: bolder;
  text-transform: uppercase;
}

.table-users {
  table {
    thead {
      tr {
        th {
          padding: 0px !important;
          margin: 0px !important;
        }
      }
    }

    tbody {
      td {
        .link-name {
          margin: auto;
          margin-left: 10px;
        }
      }
    }
  }
}

.pagination {
  padding: 0px 0px 20px 0px;
}

.row-table-image-name-link {
  display: flex;
  align-items: stretch;
}

.table-mobile {
  display: none;
}

.project-mobile-card {
  border-radius: 18px;
}

.project-mobile-card__content {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 8px 14px 8px;
}

.project-mobile-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.project-mobile-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.project-mobile-card__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 15px;
}

.project-mobile-card__label {
  color: $bg-blue;
  font-weight: 800;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .title-text {
    font-size: 2.2em;
  }

  #icon-search {
    width: 28px;
    min-width: 28px;
  }

  .card-search {
    height: auto;
    padding: 20px 16px;
    background-size: 95px;
    background-position: calc(100% + 20px) 12px;
  }

  .search-actions {
    width: 100%;
    margin-left: 0 !important;
    flex-direction: column;
    align-items: stretch !important;
  }

  .search-actions .mt-3,
  .search-actions .search {
    width: 100%;
  }

  .search-actions .search {
    margin-left: 0 !important;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
    padding-right: 0;
    padding-left: 0;
  }
}
</style>
